import { Form } from "@remix-run/react";
import {
  IconBook,
  IconBuilding,
  IconCreditCard,
  IconFileText,
  IconHelp,
  IconLogout,
  IconNews,
  IconSettings,
  IconShieldLock,
  IconSparkles,
  IconUserCircle,
  IconArrowUp,
} from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { InstallApp } from "#app/components/pwa.tsx";

import { Avatar, AvatarFallback, AvatarImage } from "#app/components/ui/avatar";
import { Button } from "#app/components/ui/button.tsx";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "#app/components/ui/dropdown-menu";
import { clearClientAuthStateOnLogout } from "#app/utils/auth.client";
import { getLocalizedDisplayName } from "#app/utils/display-name";
import { cn, getUserImgSrc } from "#app/utils/misc.tsx";
import { type useUser } from "#app/utils/user";

export type ProfilePlan = {
  name: string;
  typeLabel: string;
  isFree: boolean;
  isEnterprise: boolean;
  renewsOn: string | null;
} | null;

type ProfileDropdownProps = {
  collapsed: boolean;
  user: ReturnType<typeof useUser>;
  plan: ProfilePlan;
  isPlanLoading: boolean;
  onOpenChange: (open: boolean) => void;
  onOpenSettings: (tab: "profile" | "security" | "preferences") => void;
  onNavigate: (to: string) => void;
  onStartTour: () => void;
};

export function ProfileDropdown({
  collapsed,
  user,
  plan,
  isPlanLoading,
  onOpenChange,
  onOpenSettings,
  onNavigate,
  onStartTour,
}: ProfileDropdownProps) {
  const { t, i18n } = useTranslation(["nav", "common"]);
  const displayName = getLocalizedDisplayName(user, i18n.language) || user.id;
  const initial = user.username?.charAt(0)?.toUpperCase() ?? "?";

  const planLabel = isPlanLoading
    ? t("common:loading")
    : (plan?.name ?? t("nav:freePlan"));

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 639px)");

    const handleChange = () => {
      setIsMobile(mediaQuery.matches);
    };

    handleChange();
    mediaQuery.addEventListener("change", handleChange);

    return () => {
      mediaQuery.removeEventListener("change", handleChange);
    };
  }, []);

  return (
    <div className="fixed-account-holder absolute right-0 bottom-0 left-0 z-10 w-full border-t border-zinc-100 bg-white px-1 py-2 dark:border-border dark:bg-sidebar">
      <DropdownMenu onOpenChange={onOpenChange}>
        <DropdownMenuTrigger className="account-btn w-full cursor-pointer">
          <div className="flex w-full items-center gap-2 rounded-full px-2 py-2 transition-colors hover:bg-zinc-100 dark:hover:bg-sidebar-accent">
            <Avatar className="h-8 w-8 shrink-0">
              {user.image?.id ? (
                <AvatarImage
                  src={getUserImgSrc(user.image.id) ?? undefined}
                  alt=""
                />
              ) : null}
              <AvatarFallback className="bg-sky-600 text-white font-medium">
                {initial}
              </AvatarFallback>
            </Avatar>

            {collapsed ? null : (
              <div className="flex w-full flex-1 items-center justify-between overflow-hidden">
                <div className="min-w-0 text-left">
                  <p className="truncate text-sm font-medium">{displayName}</p>
                  <p className="truncate text-xs text-zinc-500 dark:text-zinc-400">
                    {planLabel}
                  </p>
                </div>

                <IconSettings className="mr-2 h-5 w-5 shrink-0 text-zinc-700 dark:text-zinc-300" />
              </div>
            )}
          </div>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          side={isMobile ? "top" : "right"}
          align="end"
          sideOffset={8}
          className="w-72"
        >
          <div className="mb-2 px-3 py-1.5">
            <div className="flex items-center gap-2">
              <Avatar className="h-8 w-8 shrink-0">
                {user.image?.id ? (
                  <AvatarImage
                    src={getUserImgSrc(user.image.id) ?? undefined}
                    alt=""
                  />
                ) : null}
                <AvatarFallback className="bg-sky-600 text-white font-medium">
                  {initial}
                </AvatarFallback>
              </Avatar>

              <div className="min-w-0">
                <p className="truncate font-medium text-sm">{displayName}</p>
                <p className="truncate text-xs text-muted-foreground">
                  {user.email || user.username}
                </p>
              </div>
            </div>
          </div>

          <DropdownMenuSeparator />

          {/* Plan summary — same data as the legacy account popover. */}
          <div className="px-3 py-2">
            <p className="text-[10px] font-semibold text-zinc-400 uppercase">
              {t("nav:accountMenu.currentPlan")}
            </p>

            <div className="mt-1 flex items-center justify-between gap-2">
              {isPlanLoading ? (
                <span className="inline-block h-4 w-20 animate-pulse rounded bg-zinc-100 dark:bg-zinc-800" />
              ) : (
                <span className="flex min-w-0 items-center gap-2">
                  <span className="truncate text-sm font-semibold">
                    {plan?.name ?? t("nav:freePlan")}
                  </span>
                  {plan ? (
                    <span
                      className={cn(
                        "shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium tracking-wide uppercase",
                        plan.isFree
                          ? "border border-zinc-200 bg-white text-zinc-600 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-400"
                          : "bg-[#141A42] text-white",
                      )}
                    >
                      {plan.typeLabel}
                    </span>
                  ) : null}
                </span>
              )}

              {!isPlanLoading && plan && !plan.isEnterprise ? (
                <Button
                  type="button"
                  variant="gradient"
                  size="xs"
                  onClick={() =>
                    onNavigate(plan.isFree ? "/pricing" : "/settings/billing")
                  }
                  className="py-3.5 px-2.5"
                  // className={cn(
                  //   "shrink-0 cursor-pointer rounded-md px-2.5 py-1 text-xs font-medium transition",
                  //   plan.isFree
                  //     ? "bg-[#141A42] text-white hover:bg-[#1e2a5e]"
                  //     : "border border-zinc-200 bg-white hover:bg-zinc-100",
                  // )}
                >
                  {plan.isFree
                    ? t("nav:accountMenu.upgrade")
                    : t("nav:accountMenu.manage")}

                  <IconArrowUp strokeWidth={2.5} />
                </Button>
              ) : null}
            </div>

            {!isPlanLoading && plan?.renewsOn && !plan.isEnterprise ? (
              <p className="mt-1 text-[11px] text-zinc-400">
                {t("nav:accountMenu.renewsOn", { date: plan.renewsOn })}
              </p>
            ) : null}
          </div>

          <DropdownMenuSeparator />

          <DropdownMenuItem onClick={() => onOpenSettings("profile")}>
            <IconUserCircle className="h-4 w-4" />
            <span>{t("nav:accountManagement")}</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onNavigate("/settings/billing")}>
            <IconCreditCard className="h-4 w-4" />
            <span>{t("nav:billing")}</span>
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => onNavigate("/settings/organizations")}
          >
            <IconBuilding className="h-4 w-4" />
            <span>{t("nav:accountMenu.organizationSettings")}</span>
          </DropdownMenuItem>
          {/* Beside the other settings rather than inside an organization's own
              pages: the screen is scoped to whichever organization is active,
              including a personal one, which has no organization page to sit
              under. Before this it was absent from navigation entirely and
              reachable only by typing its URL. */}
          <DropdownMenuItem onClick={() => onNavigate("/settings/models")}>
            <IconSettings className="h-4 w-4" />
            <span>
              {t("nav:accountMenu.modelSettings", {
                defaultValue: "Model settings",
              })}
            </span>
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          <DropdownMenuItem onClick={onStartTour}>
            <IconSparkles className="h-4 w-4" />
            <span>{t("nav:accountMenu.takeTour")}</span>
          </DropdownMenuItem>
          <InstallApp menu />
          <DropdownMenuItem onClick={() => onNavigate("/docs")}>
            <IconBook className="h-4 w-4" />
            <span>{t("nav:documentation")}</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onNavigate("/support")}>
            <IconHelp className="h-4 w-4" />
            <span>{t("common:support")}</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onNavigate("/blog")}>
            <IconNews className="h-4 w-4" />
            <span>{t("nav:accountMenu.releaseNotes")}</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onNavigate("/privacy")}>
            <IconShieldLock className="h-4 w-4" />
            <span>{t("nav:accountMenu.privacyPolicy")}</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onNavigate("/tos")}>
            <IconFileText className="h-4 w-4" />
            <span>{t("nav:accountMenu.termsOfService")}</span>
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          <Form
            action="/logout"
            method="POST"
            onSubmit={() => clearClientAuthStateOnLogout()}
          >
            <DropdownMenuItem
              variant="destructive"
              // Base UI's menu item is div-based, so it must be told the
              // rendered element really is a native button.
              nativeButton
              render={<button type="submit" className="w-full" />}
            >
              <IconLogout className="h-4 w-4" />
              <span>{t("nav:signOut")}</span>
            </DropdownMenuItem>
          </Form>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export default ProfileDropdown;
